import * as React from "react";
import PropTypes from "prop-types";
import * as helpers from "./helpers";
import PushUi from "../PushToSheetUi";
import Select from "./Select";
import styled from "styled-components";
import { sendData } from "../../services/sendData";
import { isSysNoComm } from "../../helpers/daily";
import {
  pushSysstats,
  addFieldIfNotFalse,
} from "../../services/backendHelpers";
import moment from "moment";

import LocalStorageKeys from "../../localStorageKeys";

const getApiToken = () => localStorage.getItem(LocalStorageKeys.userToken);

const INPUT_FIELDS = [
  { name: "comm", presentedName: "comm", type: "select", editable: true },
  {
    name: "blackout",
    presentedName: "blackout",
    type: "select",
    editable: true,
  },
  { name: "mcm", presentedName: "mcm", type: "select", editable: true },
  { name: "fct", presentedName: "fct", type: "select", editable: true },
  { name: "ovld", presentedName: "ovld", type: "select", editable: false },
  { name: "riso", presentedName: "riso", type: "select", editable: false },
  { name: "shadow", presentedName: "shadow", type: "select", editable: false },
  { name: "webbox", presentedName: "logger", type: "input", editable: true },
];

const UNCHANGED_INV_VAL = "(unchanged)";
const EMPTY_INV_VAL = "(empty)";

const commStrings = ["ok", "interrupted", "timeout", "bad", "commbug"];

const getAllowedInvVals = (maxVal = 2) => {
  const result = [UNCHANGED_INV_VAL, EMPTY_INV_VAL];
  for (let idx = 0; idx <= maxVal; ++idx) {
    result.push("" + idx);
  }

  return result;
};

const SHADOW_OPTS = getAllowedInvVals(2);
const OVLD_OPTS = getAllowedInvVals(3);
const RISO_OPTS = getAllowedInvVals(3);

const INV_STATS_PROPS = [
  { id: "invMaxDailyPower", name: "maxDailyPower" },
  { id: "invLatestStartTime", name: "latestStartTime" },
  { id: "invLatestStopTime", name: "latestStopTime" },
];

const CHAN_STATS_PROPS = [
  { id: "invMedianDcVoltage", name: "medianDcVoltage" },
];

const ResultsContainer = styled.div``;

const ActionsPanel = styled.div`
  display: flex;
  align-items: center;
`;

const TableContainer = styled.div`
  overflow: auto;
`;

// helper function to init an object with default values
function getZeroVals(data = {}, value = "0") {
  // const initVals = Object.assign(...INPUT_FIELDS.map(d => ({[d]: '0'})));
  const initVals = Object.assign(
    ...INPUT_FIELDS.filter(({ editable }) => editable).map((item) => ({
      [item.name]: value,
    })), // eh: changed '0' to a given value won't change old functionality
    data,
  );
  return initVals;
}

function createInvStatsState(invStats) {
  return !invStats
    ? null
    : invStats.map(({ id, name, disabled, vals: { shadow, ovld, riso } }) => {
        const newValues = {
          shadow: shadow !== null ? "" + shadow : UNCHANGED_INV_VAL,
          ovld: ovld !== null ? "" + ovld : UNCHANGED_INV_VAL,
          riso: riso !== null ? "" + riso : UNCHANGED_INV_VAL,
        };

        return {
          id,
          name,
          disabled,
          newValues,
        };
      });
}

class StatResultsContent extends React.Component {
  headers;

  static propTypes = {
    data: PropTypes.object,
    daily: PropTypes.shape({
      comm: PropTypes.number,
      fct: PropTypes.any,
      ovld: PropTypes.any,
      webbox: PropTypes.string,
    }),
    doPushToSheet: PropTypes.func,
    isPushOngoing: PropTypes.bool,
    pushResult: PropTypes.object,
    dataSys: PropTypes.object,
    sysDocId: PropTypes.string,
    sysId: PropTypes.string,
    dataDate: PropTypes.object,
    results: PropTypes.object,
  };

  constructor(props) {
    super(props);

    // sets all daily fields to blank if comm 3 or 4
    const initialDailyValue =
      +props.daily.comm === 3 || +props.daily.comm === 4 ? "" : "0";
    const initVals = getZeroVals(props.daily, initialDailyValue);
    const invVals = createInvStatsState(props.invStats);
    // todo: combine manual with data
    this.state = {
      isCopied: false,
      vals: initVals,
      invVals: invVals,
      statsArr: this.calcStatsArr(props),
      isPushOngoingToDB: false,
    };
    this.headers = helpers.getHeaders();
    this.handlePushClick = this.handlePushClick.bind(this);
    this.handleChange = this.handleChange.bind(this);
  }

  calcStatsArr = (props) => {
    let statsArr = null;
    if (props.dataSys.inverterMapping) {
      ({ statsArr } = props.dataSys.inverterMapping.reduce(
        (acc, inv) => {
          const currStr = `${acc.curr}`;
          const statSuffix = currStr.length === 1 ? "0" + currStr : currStr;
          acc.statsArr.push([
            ...INV_STATS_PROPS.map(
              ({ id: stat }) => props.data[stat + statSuffix] || "",
            ),
            ...CHAN_STATS_PROPS.map(({ id: stat }) => {
              return inv.channels.map((_, idx) => {
                const currStr = `${acc.curr + idx}`;
                const statSuffix =
                  currStr.length === 1 ? "0" + currStr : currStr;
                return props.data[stat + statSuffix] || "";
              });
            }),
          ]);

          acc.curr += inv.channels.length;
          return acc;
        },
        { curr: 0, statsArr: [] },
      ));
    }

    return statsArr;
  };

  // changed from `UNSAFE_componentWillReceiveProps`
  componentDidUpdate(prevProps) {
    if (
      this.props.sysId !== prevProps.sysId ||
      this.props.dataDate !== prevProps.dataDate ||
      this.props.data !== prevProps.data
    ) {
      // sets all daily fields to blank if comm 3 or 4
      const initialDailyValue =
        +this.props.daily.comm === 3 || +this.props.daily.comm === 4 ? "" : "0";
      const initVals = getZeroVals(this.props.daily, initialDailyValue);

      const invVals = createInvStatsState(this.props.invStats);
      this.setState({
        isCopied: false,
        vals: initVals,
        invVals: invVals,
        statsArr: this.calcStatsArr(this.props),
      });
    }
  }

  copyEl = (id) => {
    const contentEl = document.getElementById(id);
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(contentEl);
    selection.removeAllRanges();
    selection.addRange(range);
    document.execCommand("copy");
  };

  // copy text from the hidden form element
  copyFormEl = () => {
    const id = "__copyme";
    const formEl = document.getElementById(id);
    formEl.select();
    document.execCommand("copy");
    this.setState({
      isCopied: true,
    });
  };

  setZero = () => {
    this.setState((state) => {
      const initVals = getZeroVals();
      initVals.webbox = state.vals.webbox;
      const invVals = state.invVals.map(({ id, name, disabled }) => {
        const blankValue = "0";
        return {
          id,
          name,
          disabled,
          newValues: {
            shadow: blankValue,
            ovld: blankValue,
            riso: blankValue,
          },
        };
      });

      return {
        vals: initVals,
        invVals,
      };
    });
  };

  setBlank = () => {
    this.setState((state) => {
      const initVals = getZeroVals({}, "");
      const invVals = state.invVals.map(({ id, name, disabled }) => {
        const blankValue = UNCHANGED_INV_VAL;
        return {
          id,
          name,
          disabled,
          newValues: {
            shadow: blankValue,
            ovld: blankValue,
            riso: blankValue,
          },
        };
      });

      return {
        vals: initVals,
        invVals,
      };
    });
  };

  handlePushClick(ev) {
    const { sysId, sysDocId, dataDate } = this.props;
    const uniHeaders = INPUT_FIELDS.map(({ name }) => name).concat(
      this.headers,
    );
    const uniData = {
      ...this.state.vals,
      ...this.props.data,
    };
    const data = helpers.getDataByHeaders(uniData, uniHeaders);
    const pushQueryData = {
      data,
      sysId,
      sysDocId: sysDocId,
      date: dataDate,
      name: sysId,
      status: "Sending",
    };
    this.props.doPushToSheet(pushQueryData);
  }

  handlePushToDB = () => {
    this.setState({ isPushOngoingToDB: true }, () => {
      const date = this.props.dataDate;
      const formattedDate = moment(date).format("YYYY-MM-DD");
      const createLog = moment().format();
      const sysId = this.props.dataSys.id;

      const createPushData = (
        data,
        insights = null,
        allowEmptyInsight = false,
      ) => {
        let extra = {};
        if (
          insights &&
          typeof insights === "object" &&
          Object.keys(insights).length
        ) {
          extra.insights = Object.fromEntries(
            Object.entries(insights).filter(([key, val]) => {
              const isNumber = Number.isFinite(Number.parseFloat(val));
              // if falsey values are not allowed, all empty values will be filtered
              return allowEmptyInsight
                ? [key, val]
                : (val || isNumber) && [key, val];
            }),
          );
        }
        return {
          dataDate: formattedDate,
          logCreation: createLog,
          data: data,
          adminVersion: process.env.REACT_APP_VERSION,
          createdBy:
            localStorage.getItem(LocalStorageKeys.userEmail) || "unknown",
          statusMessage: "data model 3-Systems",
          statusCode: 0,
          systemId: this.props.dataSys.id,
          ...extra,
        };
      };

      const daily = { ...this.state.vals };
      const stats = { ...this.props.data };

      let url = "/api/logs?s3=true";
      let urlRaw = "/api/raw-logs?s3=true";
      const invStatsPutUrl = "/api/stats/inv-create-or-update";
      // fix to Kw if needed
      const systemPowerFactor = this.props.dataSys.systemPowerFactor;
      const daily_sys_power =
        stats.dailySysPower &&
        (!systemPowerFactor || +systemPowerFactor === 1
          ? stats.dailySysPower
          : stats.dailySysPower.map((hourPower) => {
              return +hourPower * +systemPowerFactor;
            }));

      const fCommString =
        (isSysNoComm(this.props.results)
          ? "nocomm"
          : commStrings[daily.comm]) || null;

      const insights = this.props.dataSys.frozen
        ? {}
        : {
            denoutput: stats.sysDayEnergy,
            daily_sys_power,
            f_comm: fCommString,
            severity: true,
          };

      // take webbox from state (a mistake since it should be in props) as "ef-logger"
      const webboxField = daily.webbox;
      if (webboxField && typeof webboxField === "string") {
        insights["ef_logger"] = webboxField;
      }

      const statsData = {
        sys_id: sysId,
        date: formattedDate,
      };

      addFieldIfNotFalse(statsData, "comm", "" + daily.comm);
      addFieldIfNotFalse(statsData, "blck", "" + daily.blackout);
      addFieldIfNotFalse(statsData, "mcm", "" + daily.mcm);
      addFieldIfNotFalse(statsData, "fct", "" + daily.fct);
      addFieldIfNotFalse(statsData, "ovld", "" + daily.ovld);
      addFieldIfNotFalse(statsData, "sys_energy", "" + stats.sysDayEnergy);
      addFieldIfNotFalse(statsData, "sys_acp_max", "" + stats.sysMaxPower);
      addFieldIfNotFalse(
        statsData,
        "sys_acp_maxshift",
        "" + stats.sysMorningPower,
      );

      pushSysstats(statsData, getApiToken()).catch((err) => console.err(err));

      sendData(
        createPushData(
          { stats: { ...this.props.data }, daily: { ...this.state.vals } },
          insights,
        ),
        url,
        getApiToken(),
      );

      if (
        Array.isArray(this.state.invVals) &&
        this.state.invVals.some((e) => !e.disabled)
      ) {
        Promise.all(
          this.state.invVals
            .filter((e) => !e.disabled)
            .map((invData) => {
              const newValues = Object.entries(invData.newValues).reduce(
                (acc, [field, val]) => {
                  if (val !== UNCHANGED_INV_VAL) {
                    if (val !== EMPTY_INV_VAL) {
                      acc[field] = val;
                    } else {
                      acc[field] = "";
                    }
                  }
                  return acc;
                },
                {},
              );
              const payload = {
                sys_id: sysId,
                inv_ids: [invData.id],
                date: formattedDate,
                newValues: newValues,
              };
              return sendData(payload, invStatsPutUrl, getApiToken(), "PUT");
            }),
        );
      }

      sendData(createPushData(this.props.results), urlRaw, getApiToken()).then(
        (res) => {
          this.setState({ isPushOngoingToDB: false });
        },
      );
    });
  };

  handleChange(name, value) {
    const newVals = {
      ...this.state.vals,
      [name]: value,
    };
    this.setState({
      vals: newVals,
    });
  }

  getDataAsTsv() {
    const uniHeaders = INPUT_FIELDS.map(({ name }) => name).concat(
      this.headers,
    );
    const uniData = {
      ...this.state.vals,
      ...this.props.data,
    };
    return helpers.getDataAsTsv(uniData, uniHeaders);
  }

  handleInvValChange = (invIdx, field, newVal) => {
    this.setState((state) => {
      const invVals = [...state.invVals];
      invVals[invIdx].newValues[field] = newVal;
      return { invVals };
    });
  };

  render() {
    if (this.props.data === null) {
      return <div>Nothing</div>;
    }

    const { statsArr } = this.state;

    return (
      <ResultsContainer>
        <TableContainer>
          <table id="__results_table">
            <thead>
              <tr>
                {INPUT_FIELDS.map((item) => (
                  <th key={item.name}>{item.presentedName}</th>
                ))}
                {this.headers.map((header, idx) => (
                  <th key={idx}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {INPUT_FIELDS.map((item) => {
                  if (item.type === "input") {
                    return (
                      <td key={item.name}>
                        <input
                          type="text"
                          name={item.name}
                          disabled={!item.editable}
                          value={this.state.vals[item.name]}
                          onChange={(ev) =>
                            this.handleChange(item.name, ev.target.value)
                          }
                        />
                      </td>
                    );
                  } else if (item.type === "select") {
                    return (
                      <Select
                        key={item.name}
                        name={item.name}
                        disabled={!item.editable}
                        value={
                          item.name in this.state.vals
                            ? this.state.vals[item.name].toString()
                            : ""
                        }
                        handleChange={this.handleChange}
                      />
                    );
                  } else {
                    return null;
                  }
                })}
                {this.headers.map((header, idx) => (
                  <td key={idx}>
                    {header in this.props.data ? (
                      <input disabled={true} value={this.props.data[header]} />
                    ) : (
                      ""
                    )}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          {this.state.invVals && (
            <table>
              <thead>
                <tr>
                  <td>
                    <b>inv</b>
                  </td>
                  <td>
                    <b>| ovld</b>
                  </td>
                  <td>
                    <b>| riso</b>
                  </td>
                  <td>
                    <b>| shadow</b>
                  </td>
                  {statsArr !== null &&
                    INV_STATS_PROPS.map(({ name: stat }) => (
                      <td key={stat}>
                        <b>| {stat}</b>
                      </td>
                    ))}
                  {statsArr !== null &&
                    CHAN_STATS_PROPS.map(({ name: stat }) => (
                      <td key={stat}>
                        <b>| {stat}</b>
                      </td>
                    ))}
                </tr>
              </thead>
              <tbody>
                {this.state.invVals.map(
                  (
                    { name, id, disabled, newValues: { shadow, ovld, riso } },
                    invIdx,
                  ) => {
                    return (
                      <tr key={invIdx}>
                        <td>
                          <b>{name || id}</b>
                        </td>
                        <td>
                          <select
                            disabled={disabled}
                            value={ovld}
                            onChange={(ev) =>
                              this.handleInvValChange(
                                invIdx,
                                "ovld",
                                ev.target.value,
                              )
                            }
                          >
                            {OVLD_OPTS.map((opt, idx) => (
                              <option key={idx}>{opt}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <select
                            disabled={disabled}
                            value={riso}
                            onChange={(ev) =>
                              this.handleInvValChange(
                                invIdx,
                                "riso",
                                ev.target.value,
                              )
                            }
                          >
                            {RISO_OPTS.map((opt, idx) => (
                              <option key={idx}>{opt}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <select
                            disabled={disabled}
                            value={shadow}
                            onChange={(ev) =>
                              this.handleInvValChange(
                                invIdx,
                                "shadow",
                                ev.target.value,
                              )
                            }
                          >
                            {SHADOW_OPTS.map((opt, idx) => (
                              <option key={idx}>{opt}</option>
                            ))}
                          </select>
                        </td>
                        {statsArr !== null &&
                          statsArr[invIdx].map((stat, idx) => {
                            if (!Array.isArray(stat)) {
                              return (
                                <td key={idx}>
                                  <input disabled={true} value={stat} />
                                </td>
                              );
                            }
                            return (
                              <td key={idx}>
                                <textarea
                                  disabled={true}
                                  value={stat.join(", ")}
                                  cols={50}
                                  rows={1}
                                />
                              </td>
                            );
                          })}
                      </tr>
                    );
                  },
                )}
              </tbody>
            </table>
          )}
        </TableContainer>
        <ActionsPanel>
          <div>
            <button onClick={this.copyFormEl} title="Copy to clipboard">
              Copy
            </button>
            {this.state.isCopied && (
              <span role="img" aria-label="thumb-up">
                👍
              </span>
            )}
          </div>
          <div>
            <button onClick={this.setBlank} title="Set daily fields to blank">
              Blank
            </button>
          </div>
          <div>
            <button onClick={this.setZero} title="Set daily fields to zero">
              Zero
            </button>
          </div>
          <PushUi
            isManualTab={false}
            handlePushClick={this.handlePushClick}
            pushResult={this.props.pushResult}
            handlePushToDB={this.handlePushToDB}
            isPushOngoingToDB={this.state.isPushOngoingToDB}
            typeDB={true}
          />
        </ActionsPanel>
        <textarea
          id="__copyme"
          readOnly={true}
          value={this.getDataAsTsv()}
          style={{ position: "fixed", left: "100%" }}
        />
      </ResultsContainer>
    );
  }
}

export default StatResultsContent;
